import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Mic, Mail, MessageCircle, Wand2, Briefcase, FileText, Users, Sparkles, Globe2, Zap, ShieldCheck, Check, ArrowRight } from "lucide-react";
import Navbar from "@/components/marketing/Navbar.jsx";
import Footer from "@/components/marketing/Footer.jsx";
import WaveformBars from "@/components/Waveform.jsx";
import LiveDemo from "@/components/marketing/LiveDemo.jsx";
import { HERO_WAVE, FEATURE_DICTATION, FEATURE_TRANSLATION, OUTPUT_MODES, LANGUAGES, TRANSFORMS } from "@/lib/brand";

const fadeUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } } };

const useCases = [
  { icon: Mail, title: "Crisp Emails", body: "Speak the gist, get a professional email with subject, greeting & sign-off." },
  { icon: MessageCircle, title: "WhatsApp Replies", body: "Friendly, short, on-brand WhatsApp replies — no typing required." },
  { icon: Wand2, title: "AI Prompts", body: "Turn rough thoughts into structured prompts for ChatGPT, Claude or Gemini." },
  { icon: Briefcase, title: "Client Briefs", body: "Voice notes become tidy client requirements with scope & deliverables." },
  { icon: FileText, title: "Blog Outlines", body: "Spoken ideas convert to publish-ready blog outlines in seconds." },
  { icon: Users, title: "Meeting Notes", body: "Capture decisions and action items with owners and next steps." },
];

const features = [
  { icon: Globe2, title: "English · हिन्दी · తెలుగు · Mixed", body: "First-class support for Indian languages including code-switching." },
  { icon: Wand2, title: "7 Output Modes", body: "Email, WhatsApp, AI Prompt, Client Brief, Blog Outline, Meeting Notes, Clean Text." },
  { icon: Zap, title: "Instant AI Transforms", body: "Polish, Professional, Shorten, Expand, Translate, Convert-to-Prompt — one click." },
  { icon: ShieldCheck, title: "Built for Indian Pros", body: "Custom dictionary preserves names, brands, technical & cultural terms perfectly." },
];

const plans = [
  { id: "free", name: "Free", price: "₹0", sub: "Forever", words: "2,000 words / week", featured: false,
    perks: ["All 4 languages", "Basic transforms", "10 history items"] },
  { id: "starter", name: "Starter", price: "₹200", sub: "/month", words: "40,000 words / month", featured: true,
    perks: ["All output modes", "All AI transforms", "Unlimited history", "Snippets & dictionary"] },
  { id: "pro", name: "Pro", price: "₹399", sub: "/month", words: "150,000 words / month", featured: false,
    perks: ["Priority AI polish", "Team-ready", "Premium support"] },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-brand-bg">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden bg-mesh">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-24 lg:pt-24 lg:pb-32 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial="hidden" animate="show" variants={fadeUp}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-brand-borderc text-xs font-medium text-brand-navy shadow-soft">
              <span className="w-2 h-2 rounded-full bg-gradient-to-br from-brand-cyan to-brand-purple animate-pulse" />
              Made in India · Built for the world
            </div>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-brand-navy leading-[1.05]">
              Speak it.{" "}
              <span className="text-gradient-primary">We draft it.</span>
            </h1>
            <p className="mt-5 text-base lg:text-lg text-brand-muted max-w-xl leading-relaxed">
              DraftVoice AI turns your spoken English, Hindi, Telugu or mixed-language thoughts into polished
              emails, WhatsApp replies, AI prompts and meeting notes — in seconds.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/signup" data-testid="hero-start-free-btn" className="btn-primary-gradient rounded-full px-7 py-3.5 font-semibold inline-flex items-center gap-2">
                Start free <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="#live-demo" data-testid="hero-try-demo-btn" className="rounded-full px-7 py-3.5 font-semibold bg-white border border-brand-borderc text-brand-navy hover:bg-brand-surface transition">
                Try live demo
              </a>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-2">
              {LANGUAGES.map((l) => (
                <span key={l.id} className="px-3 py-1.5 rounded-full bg-white border border-brand-borderc text-sm text-brand-text">
                  <span className="mr-1.5">{l.flag}</span>{l.label}
                </span>
              ))}
            </div>
          </motion.div>

          {/* Hero visual: mic card */}
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }} className="relative">
            <div className="absolute -inset-6 bg-gradient-secondary rounded-[2.5rem] blur-3xl opacity-30" />
            <div className="relative bg-white rounded-3xl border border-brand-borderc shadow-soft p-7">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs font-medium text-brand-muted uppercase tracking-wider">Live Demo</div>
                  <div className="text-sm font-semibold text-brand-navy">Recording in हिन्दी + English</div>
                </div>
                <span className="inline-flex items-center gap-2 text-xs font-semibold text-brand-purple">
                  <span className="w-2 h-2 rounded-full bg-brand-pink animate-pulse" /> LIVE
                </span>
              </div>

              <div className="relative mt-6 rounded-2xl p-8 bg-gradient-to-br from-[#F4F2FF] to-[#EAF4FF]">
                <div className="flex justify-center">
                  <div className="relative">
                    <span className="absolute inset-0 rounded-full bg-brand-purple/30 animate-pulse-ring" />
                    <button className="relative w-20 h-20 rounded-full btn-primary-gradient flex items-center justify-center shadow-glow-purple">
                      <Mic className="w-8 h-8 text-white" />
                    </button>
                  </div>
                </div>
                <WaveformBars active className="mt-6" />
                <div className="mt-4 text-center text-sm text-brand-muted">Tap to start dictating</div>
              </div>

              <div className="mt-5 grid grid-cols-3 gap-2">
                {OUTPUT_MODES.slice(0, 3).map((m) => (
                  <div key={m.id} className="rounded-xl border border-brand-borderc p-3 text-center bg-brand-bg">
                    <div className="text-xs font-semibold text-brand-navy">{m.label}</div>
                    <div className="text-[10px] text-brand-muted mt-0.5 line-clamp-1">{m.desc}</div>
                  </div>
                ))}
              </div>

              <div className="mt-5 p-4 rounded-xl bg-brand-bg border border-brand-borderc">
                <div className="text-[11px] uppercase font-semibold tracking-wider text-brand-muted">AI polished output</div>
                <div className="mt-2 text-sm text-brand-text leading-relaxed">
                  <span className="font-semibold">Subject:</span> Quick update on the Q1 launch<br />
                  Hi team, sharing a quick status — the prototype is on track and we're aiming for a soft launch by Friday…
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SOCIAL PROOF / LOGO STRIP */}
      <section className="py-10 border-y border-brand-borderc bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 flex flex-wrap items-center justify-center gap-x-10 gap-y-4 text-brand-muted text-sm">
          <span className="text-xs uppercase tracking-widest">Loved by founders, sales, support & creators across</span>
          <span className="font-semibold">Bangalore</span>·<span className="font-semibold">Mumbai</span>·<span className="font-semibold">Hyderabad</span>·<span className="font-semibold">Delhi NCR</span>·<span className="font-semibold">Chennai</span>
        </div>
      </section>

      {/* LIVE DEMO - try without signup */}
      <LiveDemo />

      {/* FEATURES */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-2xl">
            <div className="text-xs uppercase tracking-widest font-semibold text-brand-purple">Features</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-brand-navy">
              A voice-first writing studio<br />in your pocket.
            </h2>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {features.map((f, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="bg-white rounded-2xl border border-brand-borderc p-6 hover:shadow-soft transition group"
              >
                <div className="w-11 h-11 rounded-xl bg-gradient-primary flex items-center justify-center text-white shadow-glow-purple/50">
                  <f.icon className="w-5 h-5" />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-brand-navy">{f.title}</h3>
                <p className="mt-1.5 text-sm text-brand-muted leading-relaxed">{f.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS preview */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <img src={FEATURE_DICTATION} alt="Dictation" className="rounded-3xl shadow-soft object-cover w-full aspect-square" />
          <div>
            <div className="text-xs uppercase tracking-widest font-semibold text-brand-purple">How it works</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-brand-navy">
              From spoken thought to<br /> polished draft in 3 taps.
            </h2>
            <ol className="mt-8 space-y-5">
              {[
                { n: "01", t: "Tap & talk", d: "Press the mic. Speak naturally — switch languages mid-sentence." },
                { n: "02", t: "Pick an output mode", d: "Email, WhatsApp, Prompt, Brief, Notes or just Clean text." },
                { n: "03", t: "Ship it", d: "Copy, edit or transform further. Snippets & dictionary keep you fast." },
              ].map((s) => (
                <li key={s.n} className="flex gap-5">
                  <div className="text-gradient-primary text-3xl font-bold leading-none">{s.n}</div>
                  <div>
                    <div className="font-semibold text-brand-navy">{s.t}</div>
                    <div className="text-sm text-brand-muted">{s.d}</div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* USE CASES bento */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div className="max-w-xl">
              <div className="text-xs uppercase tracking-widest font-semibold text-brand-purple">Use cases</div>
              <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-brand-navy">Built for everything you type all day.</h2>
            </div>
            <Link to="/use-cases" className="text-sm font-semibold text-brand-blue hover:underline inline-flex items-center gap-1">
              See all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {useCases.map((u, i) => (
              <div key={i} className="bg-white rounded-2xl border border-brand-borderc p-6 hover:border-brand-purple/40 hover:shadow-soft transition group">
                <div className="w-10 h-10 rounded-xl bg-brand-surface flex items-center justify-center text-brand-purple group-hover:bg-gradient-primary group-hover:text-white transition">
                  <u.icon className="w-5 h-5" />
                </div>
                <h3 className="mt-4 font-semibold text-brand-navy">{u.title}</h3>
                <p className="mt-1 text-sm text-brand-muted leading-relaxed">{u.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRANSFORMS + DICTIONARY */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs uppercase tracking-widest font-semibold text-brand-purple">AI Transforms & Dictionary</div>
            <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-brand-navy">One-click rewrites that respect your vocabulary.</h2>
            <p className="mt-4 text-brand-muted leading-relaxed">
              Polish, shorten, expand, translate or convert to a prompt — all without losing your custom names, product
              terms or Indian-cultural context. Build your dictionary once, never spell it twice.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {TRANSFORMS.map((t) => (
                <span key={t.id} className="px-3 py-1.5 rounded-full bg-brand-surface text-sm font-medium text-brand-navy">
                  {t.label}
                </span>
              ))}
            </div>
          </div>
          <img src={FEATURE_TRANSLATION} alt="AI transforms" className="rounded-3xl shadow-soft object-cover w-full aspect-square" />
        </div>
      </section>

      {/* PRICING */}
      <section className="py-24" id="pricing">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 text-center">
          <div className="text-xs uppercase tracking-widest font-semibold text-brand-purple">Pricing</div>
          <h2 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-brand-navy">Premium voice writing.<br />Indian prices.</h2>
          <p className="mt-4 text-brand-muted">Start free. Upgrade when you ship more.</p>

          <div className="mt-12 grid md:grid-cols-3 gap-5 text-left">
            {plans.map((p) => (
              <div key={p.id} className={`relative rounded-3xl p-7 ${p.featured ? "bg-gradient-primary text-white shadow-glow-purple" : "bg-white border border-brand-borderc"}`}>
                {p.featured && (
                  <span className="absolute top-5 right-5 text-[10px] uppercase tracking-widest font-bold bg-white/20 text-white px-2.5 py-1 rounded-full">
                    Most popular
                  </span>
                )}
                <div className={`text-sm font-semibold ${p.featured ? "text-white/80" : "text-brand-muted"}`}>{p.name}</div>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-4xl font-bold">{p.price}</span>
                  <span className={`text-sm ${p.featured ? "text-white/80" : "text-brand-muted"}`}>{p.sub}</span>
                </div>
                <div className={`mt-1 text-sm ${p.featured ? "text-white/90" : "text-brand-navy font-medium"}`}>{p.words}</div>
                <ul className="mt-6 space-y-2.5 text-sm">
                  {p.perks.map((perk) => (
                    <li key={perk} className="flex gap-2">
                      <Check className={`w-4 h-4 mt-0.5 ${p.featured ? "text-white" : "text-brand-purple"}`} />
                      {perk}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/signup"
                  data-testid={`pricing-cta-${p.id}`}
                  className={`mt-7 block text-center rounded-full px-6 py-3 font-semibold transition ${
                    p.featured ? "bg-white text-brand-navy hover:bg-white/90" : "btn-primary-gradient"
                  }`}
                >
                  Get {p.name}
                </Link>
              </div>
            ))}
          </div>
          <div className="mt-6 text-sm text-brand-muted">
            Need a Team plan? <Link to="/contact" className="text-brand-blue font-medium hover:underline">Talk to us →</Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS placeholder */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-xs uppercase tracking-widest font-semibold text-brand-purple text-center">Loved across India</div>
          <h2 className="mt-3 text-center text-3xl sm:text-4xl font-semibold text-brand-navy">Real users, real time saved.</h2>
          <div className="mt-12 grid md:grid-cols-3 gap-5">
            {[
              { n: "Anjali R.", r: "Founder, SaaS", q: "I used to type 40 emails a day. Now I speak them while walking. It saves me 2 hours, easily." },
              { n: "Karthik S.", r: "Sales Lead", q: "Hinglish dictation just works. Client emails sound polished without me lifting a finger." },
              { n: "Ritu P.", r: "Creator", q: "Blog outlines from voice notes are gold. My drafting time dropped by 70%." },
            ].map((t, i) => (
              <div key={i} className="bg-brand-bg rounded-2xl p-6 border border-brand-borderc">
                <div className="flex gap-1 text-amber-400">{"★★★★★"}</div>
                <p className="mt-3 text-brand-text leading-relaxed">"{t.q}"</p>
                <div className="mt-4 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-gradient-primary flex items-center justify-center text-white text-sm font-bold">{t.n[0]}</div>
                  <div>
                    <div className="text-sm font-semibold text-brand-navy">{t.n}</div>
                    <div className="text-xs text-brand-muted">{t.r}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ teaser */}
      <section className="py-24">
        <div className="max-w-3xl mx-auto px-5 lg:px-8 text-center">
          <div className="text-xs uppercase tracking-widest font-semibold text-brand-purple">FAQ</div>
          <h2 className="mt-3 text-3xl sm:text-4xl font-semibold text-brand-navy">Questions, answered.</h2>
          <div className="mt-10 space-y-3 text-left">
            {[
              ["Does DraftVoice work in Hindi or Telugu?", "Yes. We officially support English, Hindi, Telugu and code-switched (Mixed/Hinglish) input."],
              ["Will my custom names & brands be preserved?", "Yes. Add them to your personal Dictionary and we will keep them spelled correctly every time."],
              ["Is my data private?", "Audio is processed for transcription only. Drafts are stored in your account and can be deleted anytime."],
            ].map(([q, a], i) => (
              <details key={i} className="bg-white rounded-2xl border border-brand-borderc p-5 group">
                <summary className="font-semibold text-brand-navy cursor-pointer flex justify-between items-center">
                  {q}
                  <ArrowRight className="w-4 h-4 group-open:rotate-90 transition" />
                </summary>
                <p className="mt-2 text-sm text-brand-muted leading-relaxed">{a}</p>
              </details>
            ))}
            <div className="text-center pt-4">
              <Link to="/faq" className="text-brand-blue font-semibold hover:underline">See all FAQs →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative pb-24">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="relative overflow-hidden rounded-[2rem] bg-gradient-primary p-10 lg:p-16 text-white text-center shadow-glow-purple">
            <div className="absolute inset-0 bg-mesh opacity-40" />
            <Sparkles className="w-8 h-8 mx-auto mb-3 opacity-90" />
            <h2 className="relative text-3xl sm:text-4xl font-bold tracking-tight">Stop typing. Start drafting with your voice.</h2>
            <p className="relative mt-4 text-white/85 max-w-xl mx-auto">Join the founders, freelancers and creators saving 2+ hours every day.</p>
            <div className="relative mt-8 flex flex-wrap gap-3 justify-center">
              <Link to="/signup" data-testid="final-cta-start-btn" className="rounded-full bg-white text-brand-navy px-7 py-3.5 font-semibold hover:bg-white/90 transition">
                Start free — no card needed
              </Link>
              <Link to="/pricing" className="rounded-full border border-white/30 text-white px-7 py-3.5 font-semibold hover:bg-white/10 transition">
                View pricing
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
